import {
  DownloadOutlined,
  FileTextOutlined,
  FormOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppRateTable from '@nxsys/components/AppRateTable';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import { defaultStatusOptions } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { assignmentActions } from '@nxsys/store/slices/assignment/reducer/assignmentSlice';
import { Assignment } from '@nxsys/store/slices/assignment/types';
import { IRELAND } from '@nxsys/store/constants/keys';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetAssignmentEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { countryList, formatStatus, getFileNameFromUrl } from '@nxsys/utils';
import { Col, Descriptions, Row, Spin, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AssignmentForm, {
  AssignmentFormRef,
  AssignmentFormValues,
} from '../../asssignment/components/AssignmentForm';

const { Text } = Typography;

const ADDRESS_TYPE = {
  INVOICING_ADDRESS: 'INVOICING_ADDRESS',
  WORK_LOCATION: 'WORK_LOCATION',
};

type Props = {
  employeeData: EmployeeRecordType;
  readOnly?: boolean;
};

const AssignmentComponent: React.FC<Props> = ({
  employeeData,
  readOnly = false,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const formRef = useRef<AssignmentFormRef>(null);

  const { payrollCountry, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { assignmentList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = assignmentList;

  const [selectedAssignmentId, setSelectedAssignmentId] = useState<
    string | null
  >(null);
  const [assignmentDetail, setAssignmentDetail] = useState<Assignment | null>(
    null,
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (employeeData?.id) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [employeeData]);

  useEffect(() => {
    if (selectedAssignmentId) {
      getDetail(selectedAssignmentId);
    }
  }, [selectedAssignmentId]);

  const handleFetchData = (params: GetAssignmentEmployeeListParams) => {
    const newParams = { ...params, id: employeeData.id };
    dispatch(employeeActions.getAssignmentEmployeeList(newParams));
  };

  const refetchList = () => {
    handleFetchData({ ...queryParams });
  };

  const getDetail = async (id: string) => {
    setLoadingDetail(true);
    const res = await dispatch(
      assignmentActions.getAssignmentById({ id }),
    ).unwrap();
    setAssignmentDetail(res.data);
    setLoadingDetail(false);
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const handleViewDetail = useCallback((id: string) => {
    setSelectedAssignmentId(id);
    setIsEditing(false);
  }, []);

  const handleBackToList = () => {
    setSelectedAssignmentId(null);
    setAssignmentDetail(null);
    setIsEditing(false);
  };

  const toggleEditing = () => setIsEditing((v) => !v);

  const handleSave = async (values: AssignmentFormValues) => {
    if (!assignmentDetail?.id) return;

    try {
      setIsUpdating(true);
      const params: any = {
        agencyRef: values.agencyName?.value,
        assignmentName: values.assignmentName ?? '',
        clientRef: values.clientRef ?? '',
        endDate: moment(values.endDate).toISOString(),
        invoicingAddress: {
          address: [
            values.invoicingAddress?.address1 ?? '',
            values.invoicingAddress?.address2 ?? '',
            values.invoicingAddress?.address3 ?? '',
          ],
          city: values.invoicingAddress?.city ?? '',
          county: values.invoicingAddress?.county ?? '',
          postCode: values.invoicingAddress?.postCode ?? '',
          country: (values.invoicingAddress?.country as any)?.value ?? '',
        },
        projectRef: values.projectRef ?? '',
        rates: values.rates?.map((rate) => ({
          ...(rate as any).rateCode.fullData,
          rateCode: (rate.rateCode as any).value,
          description: rate.description,
          rate: rate.rate,
          rateType: rate.rateType,
          vatRate: rate.vatRate,
          vatDetails: (rate.vatDetails as any).label,
        })),
        siteId: values.siteId?.value || null,
        startDate: moment(values.startDate).toISOString(),
        status: (values.status as any).value ?? '',
        subcontractorRef: values.employeeAndSubcontractor?.value ?? '',
        workLocation: {
          address: [
            values.workLocation?.address1 ?? '',
            values.workLocation?.address2 ?? '',
            values.workLocation?.address3 ?? '',
          ],
          city: values.workLocation?.city ?? '',
          county: values.workLocation?.county ?? '',
          postCode: values.workLocation?.postCode ?? '',
          country: values.workLocation?.country?.value ?? '',
        },
        file: null,
      };
      const isUploadFile = !!(values.attachment as File)?.name;
      if (isUploadFile) {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.attachment as File }),
        ).unwrap();
        params.file = res.url;
      }
      await dispatch(
        assignmentActions.updateAssignmentById({
          id: assignmentDetail?.id,
          payload: params,
        }),
      ).unwrap();
      await getDetail(assignmentDetail.id);
      refetchList();
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreate = useCallback(() => {
    navigate(`/assignments/create`, {
      query: {
        from: location.pathname,
        defaultValues: JSON.stringify({
          employeeAndSubcontractor: {
            label: `${employeeData?.forename} ${employeeData?.surname}`,
            value: employeeData.id,
          },
        }),
      },
    });
  }, [navigate, employeeData]);

  const columns: ColumnsType<Assignment> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleViewDetail(record?.id)}>
            {readOnly ? (
              <Tooltip title="View">
                <span><Icons.IconEye /></span>
              </Tooltip>
            ) : (
              <Tooltip title="Edit">
                <span><Icons.IconEdit /></span>
              </Tooltip>
            )}
          </li>
          {record?.file && (
            <li onClick={() => window.open(record.file, '_blank')}>
              <Tooltip title="Download">
                <span><Icons.IconDownload /></span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      key: 'status',
      title: messages['assignment.columns.status'],
      render: (record) => {
        const status = record?.status;
        return (
          <span className={`${formatStatus(status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          </span>
        );
      },
      width: 150,
    },
    {
      key: 'assignmentName',
      title: messages['assignment.columns.assignmentName'],
      dataIndex: 'assignmentName',
    },
    {
      key: 'subcontractorName',
      title: messages['assignment.columns.employeeSubcontractor'],
      dataIndex: 'subcontractorRef',
      render: (record) => `${record?.forename ?? ''} ${record?.surname ?? ''}`,
      width: 300,
    },
    ...(!isIreland
      ? ([
          {
            key: 'agencyName',
            title: messages['assignment.columns.agencyName'],
            dataIndex: 'agencyRef',
            render: (record) => record?.name ?? '',
          },
        ] as ColumnsType<Assignment>)
      : []),
    {
      key: 'startDate',
      title: messages['assignment.columns.startDate'],
      dataIndex: 'startDate',
      render: (record) =>
        record && moment(record) ? moment(record)?.format('DD/MM/YYYY') : '_',
    },
    {
      key: 'endDate',
      title: messages['assignment.columns.endDate'],
      dataIndex: 'endDate',
      render: (record) =>
        record && moment(record) ? moment(record)?.format('DD/MM/YYYY') : '_',
    },
  ];

  const assignmentDetailItems = [
    {
      label: messages['assignment.detail.assignmentName'],
      value: assignmentDetail?.assignmentName ?? '',
    },
    {
      label: messages['common.status'],
      value:
        defaultStatusOptions.find(
          (status) => status.value === assignmentDetail?.status,
        )?.label ?? '',
    },
    {
      label: messages['assignment.detail.agencyName'],
      value: assignmentDetail?.agencyRef?.name ?? '',
    },
    {
      label: messages['assignment.detail.projectRef'],
      value: assignmentDetail?.projectRef ?? '',
    },
    {
      label: messages['assignment.detail.employeeSubcontractor'],
      value: assignmentDetail?.subcontractorRef?.fullName ?? '',
    },
    {
      label: messages['assignment.detail.startDate'],
      value: assignmentDetail?.startDate
        ? moment(assignmentDetail?.startDate).format('DD/MM/YYYY')
        : '',
    },
    {
      label: messages['assignment.detail.clientRef'],
      value: assignmentDetail?.clientRef ?? '',
    },
    {
      label: messages['assignment.detail.endDate'],
      value: assignmentDetail?.endDate
        ? moment(assignmentDetail?.endDate).format('DD/MM/YYYY')
        : '',
    },
    {
      label: messages['assignment.detail.siteId'],
      value: assignmentDetail?.siteRef?.id ?? '',
    },
    {
      label: messages['common.attachment'],
      value: assignmentDetail?.file ? (
        <div
          className="truncate"
          onClick={() => window.open(assignmentDetail.file, '_blank')}
        >
          <FileTextOutlined className="mr-1" />
          {getFileNameFromUrl(assignmentDetail?.file)}
        </div>
      ) : (
        ''
      ),
      className: 'max-w-full cursor-pointer text-blue-500',
    },
  ];

  const renderAddressCard = (
    addressType: (typeof ADDRESS_TYPE)[keyof typeof ADDRESS_TYPE],
  ) => {
    const title =
      addressType === ADDRESS_TYPE.INVOICING_ADDRESS
        ? messages['assignment.detail.invoicingAddress']
        : messages['assignment.detail.workLocation'];

    return (
      <AppCard title={title} style={{ marginTop: 16 }}>
        <div style={{ padding: 20 }}>
          <AppDescription
            column={1}
            labelStyle={{ width: 160 }}
            bordered={false}
            colon={false}
            size="middle"
          >
            <Descriptions.Item label={messages['assignment.address.address1']}>
              {assignmentDetail?.invoicingAddress?.address?.[0] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.address2']}>
              {assignmentDetail?.invoicingAddress?.address?.[1] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.address3']}>
              {assignmentDetail?.invoicingAddress?.address?.[2] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.cityTown']}>
              {assignmentDetail?.invoicingAddress?.city ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.county']}>
              {assignmentDetail?.invoicingAddress?.county ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.postCode']}>
              {assignmentDetail?.invoicingAddress?.postCode ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.country']}>
              {assignmentDetail?.invoicingAddress?.country ?? ''}
            </Descriptions.Item>
          </AppDescription>
        </div>
      </AppCard>
    );
  };

  // Detail View
  if (selectedAssignmentId) {
    const detailLoading = loadingDetail || isUpdating;

    return (
      <Spin spinning={detailLoading}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-x-3">
            <AppBackButton onClick={handleBackToList} />
            <Typography.Title
              level={5}
              className="!mb-0 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
            >
              {assignmentDetail?.assignmentName ?? ''}
            </Typography.Title>
            <AppTagStatus
              className={`tag-popup ${formatStatus(assignmentDetail?.status ?? '')}`}
            >
              {assignmentDetail?.status}
            </AppTagStatus>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                {assignmentDetail?.file && (
                  <AppButton
                    variant="outlined"
                    type="primary"
                    name={messages['common.download']}
                    leftIcon={<DownloadOutlined />}
                    onClick={() => window.open(assignmentDetail.file, '_blank')}
                  />
                )}
                {!isAgency && !readOnly && (
                  <AppButton
                    type="primary"
                    name={messages['common.edit']}
                    leftIcon={<FormOutlined />}
                    onClick={toggleEditing}
                    disabled={detailLoading}
                  />
                )}
              </>
            ) : (
              <>
                <AppButton
                  type="primary"
                  name={messages['common.save']}
                  leftIcon={
                    <Icons.IconCheckCircle
                      color={defaultTheme.theme.palette.white}
                    />
                  }
                  onClick={() => formRef.current?.submit()}
                  loading={isUpdating}
                  disabled={isUpdating}
                />
              </>
            )}
          </div>
        </div>

        {!isEditing ? (
          <div>
            <AppCard title={messages['assignment.section.detail']}>
              <div style={{ padding: 20 }}>
                <AppDescription
                  column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                  labelStyle={{ width: 200 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  {assignmentDetailItems.map((it, idx) => (
                    <Descriptions.Item key={idx} label={it.label}>
                      <Text className={it.className}>{it.value}</Text>
                    </Descriptions.Item>
                  ))}
                </AppDescription>
              </div>
            </AppCard>

            <Row gutter={[16, 16]}>
              <Col sm={24} md={12}>
                {renderAddressCard(ADDRESS_TYPE.INVOICING_ADDRESS)}
              </Col>
              <Col sm={24} md={12}>
                {renderAddressCard(ADDRESS_TYPE.WORK_LOCATION)}
              </Col>
            </Row>

            <AppRateTable
              data={(assignmentDetail?.rates as any) ?? []}
              listHidden={['units', 'net', 'total']}
            />
          </div>
        ) : (
          <AssignmentForm
            defaultValues={{
              assignmentName: assignmentDetail?.assignmentName ?? '',
              projectRef: assignmentDetail?.projectRef ?? '',
              clientRef: assignmentDetail?.clientRef ?? '',
              status: {
                label: assignmentDetail?.status,
                value: assignmentDetail?.status,
              } as any,
              siteId: assignmentDetail?.siteRef?.id
                ? {
                    label: assignmentDetail?.siteRef?.id,
                    value: assignmentDetail?.siteRef?.id,
                  }
                : null,
              employeeAndSubcontractor: {
                label: assignmentDetail?.subcontractorRef?.fullName,
                value: assignmentDetail?.subcontractorRef?.id,
              },
              agencyName: {
                label: assignmentDetail?.agencyRef?.name,
                value: assignmentDetail?.agencyRef?.id,
              },
              startDate: assignmentDetail?.startDate,
              endDate: assignmentDetail?.endDate,
              invoicingAddress: {
                address1: assignmentDetail?.invoicingAddress?.address[0],
                address2: assignmentDetail?.invoicingAddress?.address[1],
                address3: assignmentDetail?.invoicingAddress?.address[2],
                city: assignmentDetail?.invoicingAddress?.city,
                county: assignmentDetail?.invoicingAddress?.county,
                postCode: assignmentDetail?.invoicingAddress?.postCode,
                country:
                  countryList.find(
                    (c) =>
                      c.value === assignmentDetail?.invoicingAddress?.country,
                  ) || null,
              },
              workLocation: {
                address1: assignmentDetail?.workLocation?.address[0],
                address2: assignmentDetail?.workLocation?.address[1],
                address3: assignmentDetail?.workLocation?.address[2],
                city: assignmentDetail?.workLocation?.city,
                county: assignmentDetail?.workLocation?.county,
                postCode: assignmentDetail?.workLocation?.postCode,
                country:
                  countryList.find(
                    (c) => c.value === assignmentDetail?.workLocation?.country,
                  ) || null,
              },
              rates: assignmentDetail?.rates?.map((rate) => ({
                ...(rate as any),
                rateCode: {
                  label: rate.rateCode,
                  value: rate.rateCode,
                  fullData: rate,
                },
                description: rate.description,
                rate: rate.rate,
                rateType: rate.rateType,
                vatDetails: {
                  label: rate.vatDetails,
                  value: rate.vatDetails,
                },
                vatRate: rate.vatRate,
              })),
              attachment: assignmentDetail?.file,
            }}
            formRef={formRef}
            onSubmit={handleSave}
          />
        )}
      </Spin>
    );
  }

  // List View
  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.assignment']}
      </Typography.Title>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
        {!readOnly && (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={handleCreate}
          />
        )}
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200, y: 700 }}
          titleEmpty={messages['assignment.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
    </div>
  );
};

export default AssignmentComponent;
